<template>
  <div class="live2d-page">
    <!-- 上传 -->
    <el-card shadow="never" class="card">
      <template #header>
        <div class="card-head">
          <span class="card-title">🧩 上传 Live2D 模型</span>
          <span class="card-hint">整文件夹或一个 zip 都行 · Cubism 3/4（.model3.json）和 Cubism 2（.model.json）都能用 · <b>贴图长边超过 1024 会自动降到 1024</b> · <b>不会弹「是否将文件上传到此站点」那个提示了</b></span>
        </div>
      </template>

      <el-form label-width="110px">
        <el-form-item label="模型文件夹">
          <el-input v-model="folderName" placeholder="在线上的文件夹名，中文英文都行，例如 我的皮套" maxlength="64" style="max-width: 320px" />
          <span class="tip">不填就用你选的文件夹名</span>
        </el-form-item>

        <el-form-item label="选择文件">
          <input ref="dirInput" type="file" webkitdirectory directory multiple class="hidden-input" @change="onPickDir" />
          <input ref="zipInput" type="file" accept=".zip,application/zip" class="hidden-input" @change="onPickZip" />
          <el-button @click="pickFolder" :disabled="uploading" :data-picker="canPickDir ? 'fs' : 'input'">📁 选择文件夹</el-button>
          <el-button @click="zipInput?.click()" :disabled="uploading">🗜 选择 zip</el-button>
          <span class="tip" v-if="picked.length">{{ picked.length }} 个文件，共 {{ formatSize(pickedBytes) }}</span>
        </el-form-item>

        <el-form-item label="拖进来">
          <div class="drop-zone" :class="{ over: dragOver }" @dragenter.prevent="dragOver = true"
            @dragover.prevent="dragOver = true" @dragleave.prevent="dragOver = false" @drop.prevent="onDrop">
            <template v-if="dragOver">松手就上传 ✅</template>
            <template v-else>
              把<b>模型文件夹 / zip</b>拖到这里<br />
              <span class="drop-tip">拖进来也行，跟点「选择文件夹」一个效果</span>
            </template>
          </div>
        </el-form-item>

        <el-form-item>
          <el-button type="primary" :loading="uploading" :disabled="!picked.length" @click="doUpload">开始上传</el-button>
          <el-button @click="clearPicked" :disabled="uploading">清空选择</el-button>
        </el-form-item>
      </el-form>

      <el-alert v-if="uploadMsg" :title="uploadMsg" :type="uploadOk ? 'success' : 'error'" :closable="false" show-icon />
    </el-card>

    <!-- 已安装 -->
    <el-card shadow="never" class="card">
      <template #header>
        <div class="card-head">
          <span class="card-title">🎭 已安装的模型</span>
          <el-button size="small" @click="loadList" :loading="loading">刷新</el-button>
        </div>
      </template>

      <el-empty v-if="!items.length" description="还没有上传过模型" />

      <div v-else class="model-list">
        <div v-for="it in items" :key="it.folder" class="model-item" :class="{ active: pickedInFolder(it.folder) }">
          <div class="model-main">
            <div class="model-name">
              {{ it.folder }}
              <el-tag size="small" type="info">{{ it.models.length }} 个模型文件</el-tag>
              <el-tag size="small" type="info">{{ it.files }} 文件 / {{ formatSize(it.bytes) }}</el-tag>
              <el-tag v-if="pickedInFolder(it.folder)" size="small" type="success">已在随机池</el-tag>
            </div>
            <el-checkbox-group v-model="chosen" class="model-radios">
              <el-checkbox v-for="m in it.models" :key="m.url" :label="m.url">{{ m.rel }}</el-checkbox>
            </el-checkbox-group>
            <div v-if="!it.models.length" class="warn">这个文件夹里没有 .model3.json / .model.json，用不了</div>
          </div>
          <div class="model-ops">
            <el-button v-if="canDelete()" size="small" type="danger" plain @click="doRemove(it.folder)">删除</el-button>
          </div>
        </div>
      </div>

      <div class="save-bar">
        <el-switch v-model="enabled" :active-value="1" :inactive-value="0" active-text="首页显示" />
        <el-button type="primary" :loading="saving" @click="doSave">保存</el-button>
        <span class="tip">
          已勾选 <b>{{ chosen.length }}</b> 个模型 —— <b>每次刷新随机出现一个</b>；
          保存后前台会立刻更新，不用手动刷新页面
        </span>
      </div>
    </el-card>

    <!-- 上传遮罩：进度 + 期间点不动 -->
    <teleport to="body">
      <div v-if="uploading" class="live2d-mask" @click.stop.prevent @mousedown.stop.prevent @contextmenu.prevent>
        <div class="live2d-mask-card">
          <div class="live2d-spinner"></div>
          <p class="live2d-mask-title">{{ percent >= 100 ? '服务器正在解压、把贴图压到 1024…' : '正在上传模型，请稍候…' }}</p>
          <el-progress :percentage="percent" :stroke-width="10" :status="percent >= 100 ? 'success' : ''" />
          <p class="live2d-mask-detail">{{ uploadedText }}</p>
          <p class="live2d-mask-tip">模型文件比较多、贴图比较大的时候会慢一点，别关页面</p>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import axios from 'axios';
import { broadcastSettingsChanged } from '../../../util/settingsBus';
import { canDelete } from '../../../util/permission';

const dirInput = ref(null);
const zipInput = ref(null);

const items = ref([]);
const loading = ref(false);
const saving = ref(false);
const uploading = ref(false);
const percent = ref(0);
const uploadedBytes = ref(0);
const totalBytes = ref(0);
const uploadMsg = ref('');
const uploadOk = ref(true);

const folderName = ref('');
const chosen = ref([]);        // 勾选的模型（可多选，前台每次刷新随机一个）
const enabled = ref(0);

const picked = ref([]);
const pickedBytes = computed(() => picked.value.reduce((s, f) => s + (f.size || 0), 0));

const formatSize = (n) => {
  const v = Number(n) || 0;
  if (v < 1024) return v + ' B';
  if (v < 1024 * 1024) return (v / 1024).toFixed(1) + ' KB';
  return (v / 1024 / 1024).toFixed(2) + ' MB';
};

const uploadedText = computed(() => {
  if (!uploading.value) return '';
  if (percent.value >= 100) return '文件已发送完毕，服务器正在解包/落盘…';
  if (totalBytes.value > 0) return '已上传 ' + formatSize(uploadedBytes.value) + ' / ' + formatSize(totalBytes.value);
  return '正在发送数据…';
});

const loadList = async () => {
  loading.value = true;
  try {
    const res = await axios.get('/adminapi/live2d/list');
    const d = res.data || {};
    if (d.code !== 200) {
      ElMessage.error(d.message || '读取失败');
      return;
    }
    items.value = d.items || [];
    const cur = d.current || {};
    let picks = [];
    try { picks = cur.picks ? (Array.isArray(cur.picks) ? cur.picks : JSON.parse(cur.picks)) : []; } catch (e) { picks = []; }
    if (!picks.length && cur.path) picks = [cur.path];
    // 只留服务器上真的还在的：文件夹被删掉之后，勾选项在列表里没有复选框，
    // 主人怎么点都取消不掉，一保存就报「这个模型文件不存在」——这里直接剔掉
    const alive = [];
    (d.items || []).forEach((it) => (it.models || []).forEach((m) => alive.push(m.url)));
    const kept = picks.filter((u) => alive.indexOf(u) >= 0);
    const dropped = picks.filter((u) => alive.indexOf(u) < 0);
    if (dropped.length) {
      ElMessage.warning('有 ' + dropped.length + ' 个原来勾选的模型已经不在服务器上了（' + dropped.map((u) => String(u).split('/').slice(-1)[0]).join('、') + '），已自动去掉');
    }
    chosen.value = kept;
    enabled.value = Number(cur.enabled) === 1 ? 1 : 0;
  } catch (e) {
    ElMessage.error('网络错误：' + (e.response?.data?.message || e.message));
  } finally {
    loading.value = false;
  }
};

/* ---------------- 拖拽上传 ----------------
 * 点「选择文件夹」时 Chrome 一定会弹「是否将 N 个文件上传到此站点？请仅在信任该网站时才执行此操作」——
 * 那是浏览器的安全确认，网页关不掉。拖拽走 DataTransfer，同样能读整个文件夹（含子目录）且不弹提示。 */
const dragOver = ref(false);

const readAllEntries = (reader) => new Promise((resolve) => reader.readEntries(resolve, () => resolve([])));

const walkEntry = async (entry, prefix, out) => {
  if (!entry) return;
  if (entry.isFile) {
    const file = await new Promise((resolve) => entry.file(resolve, () => resolve(null)));
    if (file) {
      try { file.__rel = prefix + file.name; } catch (e) {}
      out.push(file);
    }
    return;
  }
  if (entry.isDirectory) {
    const reader = entry.createReader();
    let batch = await readAllEntries(reader);
    while (batch && batch.length) {
      for (const it of batch) await walkEntry(it, prefix + entry.name + '/', out);
      batch = await readAllEntries(reader);
    }
  }
};

const onDrop = async (e) => {
  dragOver.value = false;
  const dt = e.dataTransfer;
  if (!dt) return;
  const files = [];
  const entries = [];
  (dt.items ? Array.from(dt.items) : []).forEach((it) => {
    const en = it.webkitGetAsEntry ? it.webkitGetAsEntry() : null;
    if (en) entries.push(en);
  });
  if (entries.length) {
    for (const en of entries) await walkEntry(en, '', files);
  } else {
    Array.from(dt.files || []).forEach((f) => files.push(f));
  }
  if (!files.length) { ElMessage.warning('没读到文件，换个方式试试'); return; }
  picked.value = files;
  const rel = files[0].webkitRelativePath || files[0].__rel || '';
  if (!folderName.value && rel && rel.indexOf('/') > 0) {
    folderName.value = rel.split('/')[0].replace(/[\\/:*?"<>|]/g, '_').slice(0, 64);
  }
  uploadMsg.value = '';
  ElMessage.success('读到 ' + files.length + ' 个文件，点「开始上传」就行');
};

/* ---------------- 选文件夹：优先走 File System Access API ----------------
 * 老写法 <input type="file" webkitdirectory> 会让 Chrome 弹
 * 「是否将 N 个文件上传到此站点？请仅在信任该网站时才执行此操作」——
 * 那是浏览器的安全确认，网页端关不掉。showDirectoryPicker() 同样是选文件夹，
 * 但走的是系统目录选择器，不弹那句话。只有浏览器不支持时才回退到老 input。 */
const canPickDir = typeof window !== 'undefined' && typeof window.showDirectoryPicker === 'function';

const readDirHandle = async (dirHandle, prefix, out) => {
  for await (const handle of dirHandle.values()) {
    if (handle.kind === 'file') {
      const file = await handle.getFile();
      try { file.__rel = prefix + handle.name; } catch (e) {}
      out.push(file);
    } else if (handle.kind === 'directory') {
      await readDirHandle(handle, prefix + handle.name + '/', out);
    }
  }
};

const pickFolder = async () => {
  if (!canPickDir) { dirInput.value?.click(); return; }   // Firefox / Safari 回退
  let handle = null;
  try {
    handle = await window.showDirectoryPicker({ mode: 'read' });
  } catch (e) {
    if (e && (e.name === 'AbortError' || e.name === 'NotAllowedError')) return;  // 主人自己取消了
    ElMessage.warning('系统文件夹选择器打不开：' + (e && (e.message || e.name) || '未知原因') + '，把文件夹拖进来也一样');
    return;
  }
  if (!handle) return;
  const files = [];
  try {
    await readDirHandle(handle, handle.name + '/', files);
  } catch (e) {
    ElMessage.error('读文件夹失败：' + (e.message || e.name));
    return;
  }
  if (!files.length) { ElMessage.warning('这个文件夹里没读到文件'); return; }
  picked.value = files;
  if (!folderName.value) folderName.value = String(handle.name || '').replace(/[\\/:*?"<>|]/g, '_').slice(0, 64);
  uploadMsg.value = '';
  ElMessage.success('读到 ' + files.length + ' 个文件，点「开始上传」就行');
};

const onPickDir = (e) => {
  const files = Array.from(e.target.files || []);
  picked.value = files;
  const first = files[0];
  if (first && first.webkitRelativePath) {
    const top = first.webkitRelativePath.split('/')[0];
    // 只挡文件系统非法字符，中文照留（以前一律替换成下划线，传「我的皮套」会变成「____」）
    if (!folderName.value) folderName.value = top.replace(/[\\/:*?"<>|]/g, '_').slice(0, 64);
  }
  uploadMsg.value = '';
};

const onPickZip = (e) => {
  picked.value = Array.from(e.target.files || []);
  const z = picked.value[0];
  if (z && !folderName.value) folderName.value = z.name.replace(/\.zip$/i, '').replace(/[\\/:*?"<>|]/g, '_').slice(0, 64);
  uploadMsg.value = '';
};

const clearPicked = () => {
  picked.value = [];
  if (dirInput.value) dirInput.value.value = '';
  if (zipInput.value) zipInput.value.value = '';
};

const doUpload = async () => {
  if (!picked.value.length) return;
  const fd = new FormData();
  const folder = (folderName.value || '').trim();
  if (folder) fd.append('folder', folder);
  for (const f of picked.value) fd.append('files', f, f.name);
  // 顺序一一对应，服务端靠它还原目录结构
  for (const f of picked.value) fd.append('paths', f.webkitRelativePath || f.__rel || f.name);

  uploading.value = true;
  percent.value = 0;
  uploadedBytes.value = 0;
  totalBytes.value = pickedBytes.value;
  uploadMsg.value = '';
  try {
    const res = await axios.post('/adminapi/live2d/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
      onUploadProgress: (ev) => {
        const loaded = Number(ev.loaded) || 0;
        const total = Number(ev.total) || 0;
        uploadedBytes.value = loaded;
        if (total > 0) {
          totalBytes.value = total;
          percent.value = Math.min(99, Math.floor((loaded / total) * 100));
        } else {
          percent.value = Math.min(99, percent.value + 1);
        }
      }
    });
    percent.value = 100;
    await new Promise((r) => setTimeout(r, 300));
    const d = res.data || {};
    uploadOk.value = d.code === 200;
    uploadMsg.value = d.message || (uploadOk.value ? '上传完成' : '上传失败');
    if (uploadOk.value) {
      clearPicked();
      folderName.value = '';       // 传完就把文件夹名输入框清空，接着传下一个
      await loadList();
      if (d.models && d.models.length) {
        const add = d.models.filter((u) => chosen.value.indexOf(u) < 0);
        if (add.length) chosen.value = chosen.value.concat(add);
      }
    }
  } catch (e) {
    uploadOk.value = false;
    uploadMsg.value = '上传失败：' + (e.response?.data?.message || e.message);
  } finally {
    uploading.value = false;
  }
};

const pickedInFolder = (folder) => chosen.value.some((u) => String(u).indexOf('/live2d/' + folder + '/') === 0);

const doSave = async () => {
  if (enabled.value === 1 && !chosen.value.length) {
    ElMessage.warning('先勾选至少一个模型再开启');
    return;
  }
  saving.value = true;
  try {
    const res = await axios.post('/adminapi/live2d/save', { picks: chosen.value, enabled: enabled.value });
    const d = res.data || {};
    if (d.code === 200) {
      ElMessage.success(d.message || '已保存');
      broadcastSettingsChanged();   // 通知正在浏览前台的标签页立刻更新
      await loadList();
      if (d.missing && d.missing.length) ElMessage.warning('顺手清掉了 ' + d.missing.length + ' 个已经不在服务器上的勾选');
    } else {
      ElMessage.error(d.message || '保存失败');
    }
  } catch (e) {
    ElMessage.error('保存失败：' + (e.response?.data?.message || e.message));
  } finally {
    saving.value = false;
  }
};

const doRemove = async (name) => {
  try {
    await ElMessageBox.confirm('确定删除模型文件夹「' + name + '」吗？删掉就找不回来了', '提示', { type: 'warning' });
  } catch (e) {
    return;
  }
  try {
    const res = await axios.post('/adminapi/live2d/delete', { folder: name });
    const d = res.data || {};
    if (d.code === 200) {
      ElMessage.success(d.message || '已删除');
      chosen.value = chosen.value.filter((u) => String(u).indexOf('/live2d/' + name + '/') !== 0);
      await loadList();
    } else {
      ElMessage.error(d.message || '删除失败');
    }
  } catch (e) {
    ElMessage.error('删除失败：' + (e.response?.data?.message || e.message));
  }
};

onMounted(loadList);
</script>

<style scoped>
.live2d-page { padding: 4px; }
.card { margin-bottom: 16px; }
.card-head { display: flex; align-items: baseline; gap: 12px; flex-wrap: wrap; }
.card-title { font-size: 15px; font-weight: 600; }
.card-hint, .tip { font-size: 12px; color: #909399; margin-left: 8px; }
.hidden-input { display: none; }
.model-list { display: flex; flex-direction: column; gap: 10px; }
.model-item { display: flex; justify-content: space-between; gap: 12px; padding: 12px 14px; border: 1px solid #ebeef5; border-radius: 8px; }
.model-item.active { border-color: #409eff; background: #f2f8ff; }
.model-name { font-weight: 600; margin-bottom: 6px; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.model-radios { display: flex; flex-direction: column; align-items: flex-start; }
.warn { color: #e6a23c; font-size: 12px; }
.save-bar { margin-top: 16px; display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }

.drop-zone { width: 100%; max-width: 460px; padding: 18px 14px; border: 2px dashed #dcdfe6; border-radius: 10px; text-align: center; color: #606266; font-size: 13px; line-height: 1.9; background: #fafafa; transition: all .15s; }
.drop-zone.over { border-color: #409eff; background: #ecf5ff; color: #409eff; }
.drop-tip { font-size: 12px; color: #909399; }

.live2d-mask { position: fixed; inset: 0; z-index: 3000; display: flex; align-items: center; justify-content: center; background: rgba(0, 0, 0, .45); cursor: wait; user-select: none; }
.live2d-mask-card { width: 330px; padding: 26px 24px 18px; border-radius: 12px; background: #fff; box-shadow: 0 12px 32px rgba(0, 0, 0, .18); text-align: center; }
.live2d-mask-title { margin: 0 0 16px; font-size: 15px; font-weight: 500; color: #303133; }
.live2d-mask-detail { margin: 10px 0 0; font-size: 13px; color: #606266; min-height: 18px; }
.live2d-mask-tip { margin: 6px 0 0; font-size: 12px; color: #909399; }
.live2d-spinner { width: 46px; height: 46px; margin: 0 auto 16px; border: 4px solid #e4e7ed; border-top-color: #409eff; border-radius: 50%; animation: live2d-spin .8s linear infinite; }
@keyframes live2d-spin { to { transform: rotate(360deg); } }
</style>
